import React, { useMemo, useState } from "react";
import { Alert } from "@mui/material";
import FileUpload from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import { bayHireService } from "@services/bayHireApi";
import type { StagedAttachment } from "@services/bayHireApi";
import { useTranslation } from "react-i18next";

/**
 * DFT-0044 `03_Bay Hire License.png` / slice U (D39): the create form's `Attachments` drop zone.
 *
 * <p>🔴 <b>This was entirely missing from the form.</b> The staged-upload endpoint
 * ({@code POST /bay-hire-requests/attachments/upload}) existed on the BFF since slice U, but no SPA
 * screen called it - a request that claims a fee exemption without this document is rejected at
 * creation (D39/AC9), so an EXEMPTED submission could never actually succeed from the UI.
 *
 * <p>{@code HOD_APPROVAL} is the ONLY attachment type this upload ever submits, unconditionally
 * (matching the wireframe) regardless of {@code paymentType} - it is the ONE attachment type the
 * pre-approve/create/update validation ever checks for ({@code BayHireRequestService}'s
 * {@code preApprove}/{@code vendorUpdate}, and {@code hodAttachmentId}/
 * {@code findLatestAttachmentByType} on the frontend). <b>Reverted 2026-08-25</b> (requester,
 * manual test on the live stack): DFT-0050 AC32/AC33 previously tagged this upload
 * {@code SUPPORTING_DOCUMENT} for every payment type except EXEMPTED, reasoning that it was
 * "general evidence, not an HOD approval" for those types - but every OTHER piece of Update
 * Request/pre-approve plumbing (this file's own {@code requestId} branch below,
 * {@code useBayHireContainer.tsx}'s {@code hodAttachmentId} diff, {@code BayHireContainer.tsx}'s
 * {@code findLatestAttachmentByType(..., "HOD_APPROVAL")} prefill, and
 * {@code BayHireRequestService#applyHodAttachmentChange}'s own type guard) already treated this
 * single control as always being the HOD_APPROVAL document, unconditionally. The AC32/AC33 split
 * left the Attachments section on Update Request looking empty (the prefill's HOD_APPROVAL lookup
 * found nothing) and made re-uploading during Update fail server-side with "attachment ... is not
 * an HOD_APPROVAL document" - confirmed live, not just in code. Tagging it SUPPORTING_DOCUMENT for
 * MONTHLY/YEARLY was therefore not a safe relabeling; it broke the one write path that reads this
 * value back as HOD_APPROVAL. Required by the confirmed requirement, not merely required at
 * validation time.
 *
 * <p>Uploads immediately on file selection rather than waiting for the form's own Submit, because
 * that is what "staged" means: the reference has to exist before the create payload can carry it.
 *
 * <p><b>Revised 2026-08-25</b> (requester, manual test): on Update Request, this used to register
 * the upload as a durable, real attachment on the LIVE request immediately on file selection (via
 * {@code uploadAttachmentFile}/{@code POST .../attachments}) - before the vendor had even clicked
 * the form's own Submit/Update button. A vendor who selected a file and then abandoned the edit
 * (or re-selected several times) left orphaned, durably-persisted attachments behind. This now
 * ALWAYS stages the blob only (SAS direct-to-Azure, `bayHireService.uploadStagedAttachment`, the
 * SAME call the create-time path already used) regardless of {@code requestId} - the durable
 * per-request registration (and the resulting {@code hodAttachmentId}) happens exactly once, at
 * submit time, from {@code useBayHireContainer.tsx}'s own {@code handleUpdateFormSubmit}, together
 * with the rest of the form's fields.
 *
 * <p>DFT-0050 (AD1/AD3): the staged upload writes bytes directly to Azure Blob Storage via a SAS
 * token minted from {@code bff-user-service} - no bytes transit any Bay Hire backend service (see
 * {@code bayHireService.uploadStagedAttachment} in {@code bayHireApi.ts}).
 */
export interface BayHireCreateAttachmentUploadProps {
  paymentType: string;
  value: StagedAttachment | null;
  onChange: (staged: StagedAttachment | null) => void;
  error?: string;
  disabled?: boolean;
  /**
   * Present on Update (`BayHireRequestForm.tsx#L297` receiving `BayHireContainer.tsx`'s
   * `requestId={updateRequest.id}`) and, since the DFT-0088 addendum (2026-09-01), Resubmit
   * (`requestId={resubmitRequest.id}`) - both flows prefill this control with a document already
   * tied to an existing request. Create and Renew never prefill one (Renew explicitly never
   * carries the parent request's attachments forward) and still omit it. DFT-0088 (AD3): used as
   * the proxy for "this document is tied to an existing request" to disable the drop zone once a
   * document is attached (AC8/AC17), without a dedicated boolean prop.
   */
  requestId?: string;
}

const HOD_APPROVAL_ATTACHMENT_TYPE = "HOD_APPROVAL";

const BayHireCreateAttachmentUpload: React.FC<BayHireCreateAttachmentUploadProps> = ({
  paymentType,
  value,
  onChange,
  error,
  disabled = false,
  requestId,
}) => {
  const { t } = useTranslation();
  const fileUploadProfiles = useFileUploadProfiles();
  const conditions = useMemo(
    () => ({ ...fileUploadProfiles.bayHireAttachment, hideErrors: false }),
    [fileUploadProfiles.bayHireAttachment],
  );
  const allowedExtensions = useMemo(
    () => (conditions.format ?? []).map((ext) => `.${ext}`),
    [conditions.format],
  );

  const [files, setFiles] = useState<UploadEntry[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleUpload = async (selected: File[]) => {
    const file = selected[selected.length - 1];
    if (!file) return;

    setUploadError(null);
    setIsUploading(true);
    try {
      // 2026-08-25 revision (see class doc): always stages the blob only, on both create and
      // Update Request - the durable per-request registration is deferred to submit time.
      const staged = await bayHireService.uploadStagedAttachment(
        file,
        HOD_APPROVAL_ATTACHMENT_TYPE,
      );
      onChange(staged);
      // DFT-0096: overwrite FileUpload's own auto-synced entry (the raw,
      // not-meaningfully-serializable File object) with the real uploaded
      // reference, mirroring SingleCertificate.tsx's onSuccess - name/url,
      // not path/relativePath, is what should survive a refresh.
      useUploadStore.getState().setFilesForKey(HOD_APPROVAL_ATTACHMENT_TYPE, [
        {
          name: staged.originalFileName ?? staged.documentUrl,
          url: staged.documentUrl,
          attachmentType: staged.attachmentType,
        },
      ]);
    } catch {
      // The file stays out of `value` on failure, so the required-for-EXEMPTED check below still
      // blocks submit - a failed upload must not silently look like a successful one.
      setFiles([]);
      useUploadStore.getState().clearFilesForKey(HOD_APPROVAL_ATTACHMENT_TYPE);
      setUploadError(t("bay-hire.form.attachment-upload-failed"));
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    setFiles([]);
    useUploadStore.getState().clearFilesForKey(HOD_APPROVAL_ATTACHMENT_TYPE);
    onChange(null);
  };

  return (
    <div className="py-2" data-testid="bay-hire-form-attachments-section">
      <h4 className="text-sm text-gray-700 mb-2">
        {t("bay-hire.form.attachments")}
        {paymentType === "EXEMPTED" ? " *" : ""}
      </h4>

      {uploadError && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="bay-hire-form-attachment-upload-error">
          {uploadError}
        </Alert>
      )}

      {/* DFT-0088 (AC8/AC17, AD3): once a document is attached on Update or Resubmit (`requestId`
          present - the proxy for "this document is tied to an existing request", threaded
          through by BayHireContainer.tsx on both those call sites), the drop zone now disables,
          matching Cancel's own disabled-dropzone behaviour in the same state - a second document
          can no longer be dropped directly over an existing one; Remove must be used first.
          Create/Renew (no `requestId`, never prefill a document) are unaffected by construction
          (AC9). */}
      <FileUpload
        testId="bay-hire-form-attachment-upload"
        // DFT-0096: keyed by attachment type (mirrors SingleCertificate.tsx's
        // useUploadStore key convention) so a staged-but-not-yet-submitted
        // HOD document survives a refresh via the shared, localStorage-backed
        // useUploadStore (FileUpload's own built-in integration - see its
        // `name` prop). handleUpload overwrites this key's entry with the
        // real {name, url} once the upload succeeds.
        name={HOD_APPROVAL_ATTACHMENT_TYPE}
        onUpload={handleUpload}
        onRemoveFile={handleRemove}
        files={files}
        setFiles={setFiles}
        maxFiles={1}
        allowedExtensions={allowedExtensions}
        conditions={conditions}
        disabled={disabled || isUploading || (!!requestId && !!value)}
        // DFT-0088 addendum (2026-09-01): matches the actual `bayHireAttachment` allow-list -
        // see FileUpload.constants.ts's own comment on that profile. Uses the same
        // `dragOrDropDescription` key every non-Bay-Hire consumer of this identical
        // pdf/jpg/jpeg/png/bmp/gif/excel/word/10MB shape already uses, instead of a bespoke
        // string that can silently drift from the real allow-list.
        uploadDescription={t("upload.dragOrDropDescription")}
      />

      {/* DFT-0088 (G4): matches Cancel/Add Receipt's SingleFile presentation exactly, replacing
          the previous bespoke Box/Typography/Button card (itself a 2026-08-05 bug fix that had
          only matched PreApproveDialog's own bespoke card, not Cancel/Add Receipt's). */}
      {value && (
        <SingleFile
          testId="bay-hire-form-attachment"
          file={{ name: value.originalFileName ?? value.documentUrl }}
          fieldName="bay-hire-form-attachment-upload"
          setFiles={() => setFiles([])}
          removeFn={handleRemove}
          disabled={disabled}
        />
      )}

      {error && (
        <p className="text-xs text-red-600 mt-1" data-testid="bay-hire-form-attachments-error">
          {error}
        </p>
      )}
    </div>
  );
};

export default BayHireCreateAttachmentUpload;
